import type {
  OpencodeMessagePart,
  OpencodeToolPart,
  SessionMessageWithParts,
} from "schemas/generated/opencode";

import React from "react";

import { useToolCallSubagentSessionId } from "@agent-ui/features/chat/store/SessionStoreContext";
import { BotIcon } from "@replo/design-system/icons/animated/BotIcon";
import { CircleCheckIcon } from "@replo/design-system/icons/animated/CircleCheckIcon";
import twMerge from "@replo/design-system/utils/twMerge";
import { AlertCircle, ChevronRight } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { formatDuration } from "replo-utils/lib/datetime";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

import { ToolRow } from "./ToolRow";

export const SubagentTaskToolRow = ({
  toolPart,
  subagentMessages,
}: {
  toolPart: OpencodeToolPart;
  subagentMessages: SessionMessageWithParts[];
}) => {
  const subagentSessionId = useToolCallSubagentSessionId(toolPart.callID);
  const state = toolPart.state;
  const isRunning = state.status === "pending" || state.status === "running";
  const [isOpen, setIsOpen] = React.useState(isRunning);

  React.useEffect(() => {
    if (isRunning) {
      setIsOpen(true);
    }
  }, [isRunning]);

  const duration = exhaustiveSwitch(
    state,
    "status",
  )({
    pending: () => null,
    running: () => null,
    completed: (s) => formatDuration(s.time.end - s.time.start),
    error: (s) => formatDuration(s.time.end - s.time.start),
  });

  const statusIcon = (() => {
    if (state.status === "completed") {
      return <CircleCheckIcon size={14} animateOnMount />;
    }
    if (state.status === "error") {
      return <AlertCircle size={14} className="text-red" />;
    }
    return <BotIcon size={14} loop />;
  })();

  const subagentToolParts = subagentMessages
    .filter((m) => m.message.opencodeSessionId === subagentSessionId)
    .flatMap((m) => m.parts)
    .filter((p): p is OpencodeMessagePart & { partData: OpencodeToolPart } => {
      return p.partData.type === "tool";
    })
    .map((p) => p.partData);

  return (
    <div className="flex flex-col w-full">
      <button
        type="button"
        className="flex items-center justify-between w-full min-h-8"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <div className="flex items-center gap-2">
          {statusIcon}
          <ChevronRight
            size={12}
            className={twMerge(
              "text-muted transition-transform duration-200",
              isOpen && "rotate-90",
            )}
          />
          <span
            className={twMerge(
              "text-sm text-default",
              isRunning && "text-shimmer",
              state.status === "error" && "text-red",
            )}
          >
            {typeof toolPart.state.input?.description === "string"
              ? toolPart.state.input.description
              : "Subagent task"}
          </span>
        </div>
        {duration && (
          <span className="text-xs text-muted whitespace-nowrap">
            {duration}
          </span>
        )}
      </button>

      <AnimatePresence initial={false}>
        {isOpen && subagentToolParts.length > 0 && (
          <motion.div
            key="subagent-content"
            className="flex flex-col pl-7 overflow-hidden"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            {subagentToolParts.map((part, idx) => (
              <ToolRow key={`${part.callID}-${idx}`} toolPart={part} />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
